import { useMutation } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useMemo } from 'react'
import { toast } from 'sonner'
import { strapiPut } from '@/lib/strapi'
import { db } from '@/lib/db/database'
import type { CustomerOrder, CustomerOrderFilters, CustomerOrderStatus } from '@/lib/types/customerOrder'

function applyFilters(orders: CustomerOrder[], filters: CustomerOrderFilters): CustomerOrder[] {
  let result = orders

  if (filters.date) result = result.filter((o) => o.date === filters.date)
  if (filters.dateFrom) result = result.filter((o) => o.date >= filters.dateFrom!)
  if (filters.dateTo) result = result.filter((o) => o.date <= filters.dateTo!)
  if (filters.status?.length) result = result.filter((o) => filters.status!.includes(o.status))
  if (filters.source) result = result.filter((o) => o.source === filters.source)

  if (filters.search) {
    const q = filters.search.toLowerCase()
    result = result.filter(
      (o) =>
        o.guest?.name?.toLowerCase().includes(q) ||
        o.guest?.phone?.toLowerCase().includes(q) ||
        o.guest?.email?.toLowerCase().includes(q),
    )
  }

  result = result.slice().sort((a, b) => a.dateTime.localeCompare(b.dateTime))

  const page = filters.page ?? 1
  const pageSize = filters.pageSize ?? 50
  return result.slice((page - 1) * pageSize, page * pageSize)
}

export function useCustomerOrders(filters: CustomerOrderFilters = {}) {
  const allOrders = useLiveQuery(() => db.customerOrders.toArray(), [])

  const data = useMemo(() => {
    if (allOrders === undefined) return undefined
    return applyFilters(allOrders, filters)
  }, [
    allOrders,
    filters.date,
    filters.dateFrom,
    filters.dateTo,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.status),
    filters.source,
    filters.search,
    filters.page,
    filters.pageSize,
  ])

  return {
    data:
      data !== undefined
        ? { data, meta: { pagination: { total: data.length, page: filters.page ?? 1, pageSize: filters.pageSize ?? 50, pageCount: 1 } } }
        : undefined,
    isLoading: data === undefined,
  }
}

export function useUpdateCustomerOrderStatus() {
  return useMutation({
    mutationFn: async ({
      documentId,
      status,
      estimatedReadyMinutes,
    }: {
      documentId: string
      status: CustomerOrderStatus
      estimatedReadyMinutes?: number
    }) => {
      const existing = await db.customerOrders.get(documentId)
      const oldStatus = existing?.status
      const patch: Partial<CustomerOrder> = { status }
      if (estimatedReadyMinutes != null) patch.estimatedReadyMinutes = estimatedReadyMinutes

      await db.customerOrders.update(documentId, patch)
      try {
        const resp = await strapiPut<CustomerOrder>(`/api/customer-orders/${documentId}`, {
          data: patch,
        })
        const order = (resp as any).data ?? resp
        if (order?.documentId) await db.customerOrders.put(order)
        return resp
      } catch (err) {
        if (oldStatus !== undefined) await db.customerOrders.update(documentId, { status: oldStatus })
        throw err
      }
    },
    onError: () => toast.error('Failed to update order'),
    onSuccess: () => toast.success('Order updated'),
  })
}
